import axios from 'axios';
import { getCookie } from '@/shared/utils/cookies';
import { getSelectedTenant } from '@/shared/utils/tenantStorage';
import { formatApiErrorMessage } from '@/shared/utils/errorUtils';

// Timeout configuration (in milliseconds)
const TIMEOUT_CONFIG = {
  default: 30000, // 30 seconds for most requests
  upload: 120000, // 2 minutes for file uploads
  download: 120000, // 2 minutes for downloads
  longRunning: 60000, // 1 minute for reports/analytics
};

const api = axios.create({
  baseURL: import.meta.env.VITE_BASE_URL,
  timeout: TIMEOUT_CONFIG.default,
  responseType: 'json',
  responseEncoding: 'utf8',
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

export const createRequestWithTimeout = (timeout) => {
  return axios.create({
    ...api.defaults,
    timeout,
  });
};

export { TIMEOUT_CONFIG };

// Auth callbacks (will be set by auth config)
let authCallbacks = {
  getToken: () => null,
  refreshToken: () => Promise.resolve(null),
  clearTokens: () => {},
};

export const setAuthCallbacks = (callbacks) => {
  authCallbacks = {
    ...authCallbacks,
    ...callbacks,
  };
};

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add access token to headers if exists
    const token = authCallbacks.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add refresh token to headers if needed
    const refreshToken = getCookie('refresh_token');
    if (refreshToken && config.url?.includes('/refresh-token')) {
      config.headers['refresh-token'] = refreshToken;
    }

    // Add tenant ID to headers if exists
    const tenantId = config.tenantId ?? getSelectedTenant();
    if (tenantId) {
      config.headers['X-Tenant-Id'] = tenantId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Show success toast from backend message (unless disabled)
    if (
      response.data?.message &&
      window.showToast &&
      response.config?.showToast !== false
    ) {
      window.showToast({
        message: response.data.message,
        type: 'success',
      });
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const isAuthEndpoint = originalRequest?.url?.includes('/auth/');
    const isRefreshEndpoint = originalRequest?.url?.includes('/refresh-token');

    // Network error
    if (!error.response) {
      if (!navigator.onLine) {
        const errorData = {
          code: 'NETWORK_ERROR',
          message: 'اتصال به اینترنت برقرار نیست',
          success: false,
        };
        if (window.showToast && originalRequest?.showToast !== false) {
          window.showToast({
            message: errorData.message,
            type: 'error',
          });
        }
        return Promise.reject(errorData);
      }
      const errorData = {
        code: 'SERVER_ERROR',
        message: 'سرور در دسترس نیست',
        success: false,
      };
      if (window.showToast && originalRequest?.showToast !== false) {
        window.showToast({
          message: errorData.message,
          type: 'error',
        });
      }
      return Promise.reject(errorData);
    }

    // Timeout error
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      const errorData = {
        code: 'TIMEOUT_ERROR',
        message: 'زمان درخواست به پایان رسید. لطفا دوباره تلاش کنید',
        success: false,
      };
      if (window.showToast && originalRequest?.showToast !== false) {
        window.showToast({
          message: errorData.message,
          type: 'error',
        });
      }
      return Promise.reject(errorData);
    }

    // Handle 401 Unauthorized - Try to refresh token
    if (
      error.response.status === 401 &&
      !originalRequest._retry &&
      !isAuthEndpoint &&
      !isRefreshEndpoint
    ) {
      originalRequest._retry = true;

      try {
        await authCallbacks.refreshToken();
        // Retry the original request with new token
        return api(originalRequest);
      } catch (refreshError) {
        authCallbacks.clearTokens();
        if (window.showToast) {
          window.showToast({
            message: 'نشست شما منقضی شده است. لطفا دوباره وارد شوید',
            type: 'error',
          });
        }
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // If refresh token itself fails with 401, redirect to login
    if (error.response.status === 401 && isRefreshEndpoint) {
      authCallbacks.clearTokens();
      if (window.showToast) {
        window.showToast({
          message: 'نشست شما منقضی شده است. لطفا دوباره وارد شوید',
          type: 'error',
        });
      }
      window.location.href = '/login';
      return Promise.reject(error.response.data);
    }

    // Server error response
    const { status, data } = error.response;

    const errorData = data || {
      code: status,
      success: false,
      message: 'خطای سرور',
      errors: null,
    };

    // Show error toast from backend message (unless disabled)
    if (window.showToast && originalRequest?.showToast !== false) {
      window.showToast({
        message: formatApiErrorMessage(errorData),
        type: 'error',
      });
    }

    return Promise.reject(errorData);
  },
);

export const apiSuccessOutput = ({ data }) => {
  if (!data.success && data.message) {
    throw new Error(data.message);
  }
  const { code, message, success, meta, data: responseData } = data;
  return {
    code,
    message,
    success,
    meta,
    data: responseData,
  };
};

export default api;
